/* eslint-disable */
import { useMemo, useState } from 'react';
import { TextField, InputAdornment } from '@mui/material';
import Checkbox from 'rsuite/Checkbox';
import {
	Search, ChevronDown, ChevronRight, ScanLine, Hash
} from 'lucide-react';

/**
 * Step 3 — pick which asset types to count, grouped by asset category.
 *
 * The backend only ever returns types marked `inwarded`; a type that never
 * enters store stock has no ledger balance to audit against. `categoryName`
 * comes down on each type (a display-only dataclass field), so the grouping
 * needs no second call.
 *
 * Traceable types are counted by scanning each unit, non-traceable ones by a
 * typed figure — the badge says which, because it changes what the person at
 * the store actually has to do.
 */

// The label spells out the mechanism: a traceable type is counted by scanning
// each unit's QR, a non-traceable one by typing a figure. "Scanned"/"Counted"
// alone left people guessing which was which.
const COUNT_METHODS = [
	{ id: 'ALL', label: 'All' },
	{ id: 'SCANNED', label: 'Scanned (QR)' },
	{ id: 'COUNTED', label: 'Counted (Non-QR)' },
];

const UNCATEGORISED = 'Uncategorised';

const AssetAuditStep3 = ({
	assetTypes = [],
	isLoading = false,
	hasError = false,
	retryFetch,
	selectedAssetTypeIds = [],
	setSelectedAssetTypeIds,
}) => {
	const [search, setSearch] = useState('');
	const [countMethod, setCountMethod] = useState('ALL');
	const [categoryFilter, setCategoryFilter] = useState('ALL');
	// Groups start CLOSED. An entry is written only when the user opens or
	// closes one, so `undefined` means closed — that way a newly-appearing
	// category (after a filter change) is closed too, rather than springing
	// open because it has no entry yet.
	const [collapsed, setCollapsed] = useState({});

	const selectedSet = useMemo(
		() => new Set(selectedAssetTypeIds),
		[selectedAssetTypeIds]
	);

	const categories = useMemo(() => {
		const names = new Set();
		assetTypes.forEach((type) => names.add(type?.categoryName || UNCATEGORISED));
		return [...names].sort();
	}, [assetTypes]);

	const visible = useMemo(() => {
		const term = search.trim().toLowerCase();
		return assetTypes.filter((type) => {
			const name = (type?.name ?? '').toLowerCase();
			const code = (type?.code ?? '').toLowerCase();
			if (term && !name.includes(term) && !code.includes(term)) return false;
			if (countMethod === 'SCANNED' && !type?.traceable) return false;
			if (countMethod === 'COUNTED' && type?.traceable) return false;
			if (
				categoryFilter !== 'ALL' &&
				(type?.categoryName || UNCATEGORISED) !== categoryFilter
			) {
				return false;
			}
			return true;
		});
	}, [assetTypes, search, countMethod, categoryFilter]);

	// [{ name, types: [] }], categories alphabetical, types alphabetical within.
	const groups = useMemo(() => {
		const byCategory = new Map();
		visible.forEach((type) => {
			const key = type?.categoryName || UNCATEGORISED;
			if (!byCategory.has(key)) byCategory.set(key, []);
			byCategory.get(key).push(type);
		});
		return [...byCategory.entries()]
			.sort((a, b) => a[0].localeCompare(b[0]))
			.map(([name, types]) => ({
				name,
				types: types.sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? '')),
			}));
	}, [visible]);

	const toggle = (id) => {
		if (selectedSet.has(id)) {
			setSelectedAssetTypeIds(selectedAssetTypeIds.filter((x) => x !== id));
		} else {
			setSelectedAssetTypeIds([...selectedAssetTypeIds, id]);
		}
	};

	// Selecting/clearing only ever touches what is currently visible, so a
	// search or filter can never silently wipe a selection made under a
	// different one.
	const toggleMany = (types) => {
		const ids = types.map((type) => type.id);
		const allOn = ids.every((id) => selectedSet.has(id));
		if (allOn) {
			const drop = new Set(ids);
			setSelectedAssetTypeIds(selectedAssetTypeIds.filter((id) => !drop.has(id)));
		} else {
			setSelectedAssetTypeIds([...new Set([...selectedAssetTypeIds, ...ids])]);
		}
	};

	const allVisibleSelected =
		visible.length > 0 && visible.every((type) => selectedSet.has(type.id));

	// Whole-list actions, deliberately separate from the "shown" ones: these
	// ignore every filter, so the label says "all" and the count next to it
	// says how many that is.
	const selectEverything = () =>
		setSelectedAssetTypeIds(assetTypes.map((type) => type.id));
	const clearEverything = () => setSelectedAssetTypeIds([]);

	return (
		<div className='flex flex-col h-full min-h-0 gap-3 p-1'>
			{/* Filter bar */}
			<div className='flex flex-wrap items-center gap-3'>
				<TextField
					size='small'
					placeholder='Search name or code'
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					className='flex-1 min-w-[14rem]'
					InputProps={{
						startAdornment: (
							<InputAdornment position='start'>
								<Search className='size-4 text-gray-400' />
							</InputAdornment>
						),
					}}
				/>

				<div className='flex p-1 rounded-lg bg-gray-100'>
					{COUNT_METHODS.map((method) => (
						<button
							key={method.id}
							type='button'
							onClick={() => setCountMethod(method.id)}
							className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
								countMethod === method.id
									? 'bg-uy-color text-white shadow'
									: 'text-gray-600 hover:text-gray-900'
							}`}
						>
							{method.label}
						</button>
					))}
				</div>

				<select
					value={categoryFilter}
					onChange={(e) => setCategoryFilter(e.target.value)}
					className='px-2 py-2 text-sm bg-white border rounded-md min-w-[10rem]'
				>
					<option value='ALL'>All categories</option>
					{categories.map((name) => (
						<option key={name} value={name}>
							{name}
						</option>
					))}
				</select>
			</div>

			{/* Summary strip */}
			<div className='flex flex-wrap items-center justify-between gap-2 px-1'>
				<span className='text-sm'>
					<b>{selectedAssetTypeIds.length}</b> selected
					<span className='text-gray-500'>
						{' '}
						· showing {visible.length} of {assetTypes.length}
						{isLoading ? ' · loading…' : ''}
					</span>
				</span>
				<div className='flex items-center gap-2'>
					<button
						type='button'
						className='px-3 py-1 text-xs font-medium text-white rounded-md bg-teal-600 hover:bg-teal-700 disabled:opacity-40'
						onClick={selectEverything}
						disabled={
							assetTypes.length === 0 ||
							selectedAssetTypeIds.length === assetTypes.length
						}
					>
						Select all ({assetTypes.length})
					</button>
					<button
						type='button'
						className='px-3 py-1 text-xs font-medium bg-white border rounded-md hover:bg-gray-50 disabled:opacity-40'
						onClick={clearEverything}
						disabled={selectedAssetTypeIds.length === 0}
					>
						Clear all
					</button>
					{visible.length > 0 && visible.length !== assetTypes.length && (
						<button
							type='button'
							className='text-xs font-medium text-teal-700 underline'
							onClick={() => toggleMany(visible)}
						>
							{allVisibleSelected ? 'Clear shown' : 'Select shown'}
						</button>
					)}
				</div>
			</div>

			{/* Grouped list */}
			{hasError && assetTypes.length === 0 ? (
				// A failed fetch must not read as "nothing is flagged for audit" —
				// that sends the user off to edit the asset-type master instead.
				<div className='flex flex-col items-center gap-2 p-4 text-sm text-center text-gray-500 border border-dashed rounded-md'>
					<span>Couldn’t load asset types.</span>
					{retryFetch && (
						<button
							type='button'
							className='px-3 py-1 text-xs font-medium bg-white border rounded-md hover:bg-gray-50'
							onClick={retryFetch}
						>
							Retry
						</button>
					)}
				</div>
			) : isLoading && assetTypes.length === 0 ? (
				<div className='flex flex-col flex-1 min-h-0 overflow-hidden border rounded-md'>
					{[0, 1, 2, 3, 4].map((row) => (
						<div
							key={row}
							className='flex items-center gap-2 px-3 py-2 border-b animate-pulse last:border-b-0'
						>
							<div className='w-4 h-4 rounded bg-gray-200' />
							<div className='w-1/3 h-3 rounded bg-gray-200' />
							<div className='w-24 h-3 ml-auto rounded bg-gray-100' />
						</div>
					))}
				</div>
			) : !isLoading && assetTypes.length === 0 ? (
				<div className='p-4 text-sm text-center text-gray-500 border border-dashed rounded-md'>
					No asset types are marked as “Adds To Store Stock”, so there is
					nothing to audit. Set that flag on an asset type first.
				</div>
			) : groups.length === 0 ? (
				<div className='p-4 text-sm text-center text-gray-500 border border-dashed rounded-md'>
					No asset types match these filters.
				</div>
			) : (
				<div className='flex flex-col flex-1 min-h-0 overflow-y-auto border rounded-md'>
					{groups.map((group) => {
						const groupIds = group.types.map((type) => type.id);
						const selectedInGroup = groupIds.filter((id) =>
							selectedSet.has(id)
						).length;
						const isCollapsed = collapsed[group.name] ?? true;
						return (
							<div key={group.name}>
								<div className='flex items-center gap-2 px-3 py-2 border-b bg-gray-50'>
									<button
										type='button'
										onClick={() =>
											setCollapsed((prev) => ({
												...prev,
												[group.name]: !isCollapsed,
											}))
										}
										className='flex items-center gap-1 text-sm font-semibold text-gray-700'
									>
										{isCollapsed ? (
											<ChevronRight className='size-4' />
										) : (
											<ChevronDown className='size-4' />
										)}
										{group.name}
									</button>
									<span className='text-xs text-gray-500'>
										{selectedInGroup}/{group.types.length}
									</span>
									<button
										type='button'
										className='ml-auto text-xs text-teal-700 underline'
										onClick={() => toggleMany(group.types)}
									>
										{selectedInGroup === group.types.length
											? 'Clear'
											: 'Select all'}
									</button>
								</div>

								{!isCollapsed &&
									group.types.map((type) => {
										const isSelected = selectedSet.has(type.id);
										return (
											<label
												key={type.id}
												className={`flex items-center min-w-0 gap-2 px-3 py-1 border-b cursor-pointer last:border-b-0 ${
													isSelected
														? 'bg-yellow-50'
														: 'hover:bg-gray-50'
												}`}
											>
												<Checkbox
													checked={isSelected}
													onChange={() => toggle(type.id)}
												/>
												<span
													className='min-w-0 text-sm truncate'
													title={type.name}
												>
													{type.name}
												</span>
												{type.code && (
													<span className='text-xs text-gray-600'>
														{type.code}
													</span>
												)}
												<span className='ml-auto inline-flex items-center gap-1 text-[10px] px-2 py-[2px] rounded-full whitespace-nowrap bg-slate-100 text-slate-700'>
													{type.traceable ? (
														<ScanLine className='size-3' />
													) : (
														<Hash className='size-3' />
													)}
													{type.traceable
														? 'Scanned (QR)'
														: 'Counted (Non-QR)'}
												</span>
											</label>
										);
									})}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

export default AssetAuditStep3;
